import { AutoResizeInput } from '@/app/(webapp)/_components/auto-resize-input';
import CustomCheckBox from '@/app/(webapp)/_components/modals/custom-checkbox';
import { localeMap } from '@/app/data/localMap';
import AppButton from '@/components/ui/app-button';
import { IconDragAndDrop, IconLoading } from '@/components/ui/icons';
import { Skeleton } from '@/components/ui/skeleton';
import type { Program as GraphQLProgram, LanguageIsoCode } from '@/graphql/graphql';
import { DetailsObject } from '@/graphql/scalars';
import { formatDuration } from '@/lib/utils';
import { DndContext, type DragEndEvent, MouseSensor, TouchSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import iconPlayBorderWhite from '@public/icons/icon-play-border-white.svg';
import iconInfoWhite from '@public/icons/info.svg';
import Image from 'next/image';
import { useEffect, useRef, useState } from 'react';
import type { EpisodesListProps, SortableItemProps } from './interfaces';

const episodeSkeletonKeys = [
    'episode-skeleton-1',
    'episode-skeleton-2',
    'episode-skeleton-3',
    'episode-skeleton-4',
    'episode-skeleton-5',
];

const SortableItem: React.FC<SortableItemProps> = ({ id, disabled = false, children }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id, disabled });

    const style: React.CSSProperties = {
        transform: CSS.Transform.toString(transform),
        transition,
        cursor: disabled ? 'default' : isDragging ? 'grabbing' : 'grab',
        willChange: isDragging ? 'transform' : 'auto ',
    };

    return (
        <div ref={setNodeRef} style={style} {...attributes} className={isDragging ? 'z-50' : ''}>
            {children({ listeners: !disabled ? listeners : undefined })}
        </div>
    );
};

export const EpisodesList = ({
    isAnon,
    isSeller,
    mainProgramTitle,
    episodes,
    isEditView,
    isReadOnly,
    user,
    locale,
    formInputClassName,
    editViewEpisodeClassName,
    t,
    openInfoModal,
    setTechnicalInfoCardActive,
    checkDetailsAvailability,
    getMultiLanguageValue,
    setSelectedEpisode,
    setShowModalCart,
    setIsNotRegistered,
    setEpisodes,
    updateEpisodeOrder,
    playMaster,
    containerClassName,
    updateEpisodeTitle,
    buttonRef,
    showPurchaseButton,
    deselectedEpisodesIds,
    onSelectedEpisodesChange,
    totalCount,
    loadingInitial,
    loadingMore,
}: EpisodesListProps) => {
    const sensors = useSensors(
        useSensor(MouseSensor, {
            activationConstraint: {
                distance: 10,
            },
        }),
        useSensor(TouchSensor, {
            activationConstraint: {
                delay: 250,
                tolerance: 5,
            },
        }),
    );

    useEffect(() => {
        if (!setTechnicalInfoCardActive) {
            return;
        }
        const hasAtLeastOneEpisodeWithTechInfo = episodes.some((episode) => checkDetailsAvailability(episode));

        setTechnicalInfoCardActive(hasAtLeastOneEpisodeWithTechInfo);
    }, [episodes, setTechnicalInfoCardActive, checkDetailsAvailability]);

    const handleDragEnd = async (event: DragEndEvent) => {
        const { active, over } = event;

        if (over && active.id !== over.id) {
            const oldIndex = episodes.findIndex((ep) => ep.identifier.id === active.id);
            const newIndex = episodes.findIndex((ep) => ep.identifier.id === over.id);

            const newOrder = arrayMove(episodes, oldIndex, newIndex);

            const optimisticOrder = newOrder.map((episode, index) => ({
                ...episode,
                episodeNumber: index + 1,
            }));

            setEpisodes(optimisticOrder);

            try {
                await updateEpisodeOrder(newOrder);
            } catch (_error) {
                setEpisodes(episodes);
            }
        }
    };

    const titleRef = useRef<HTMLElement>(null);

    const [buttonMinW, setButtonMinW] = useState<string>('');

    useEffect(() => {
        if (buttonRef?.current) {
            const rect = buttonRef.current.getBoundingClientRect();
            setButtonMinW(`${rect.width}px`);
        }
    }, [buttonRef]);

    const formatProgramPrice = (locale: string, value: number) => {
        const price = Number.isNaN(value) ? 0 : value;
        return new Intl.NumberFormat(localeMap[locale], {
            style: 'currency',
            currency: 'USD',
            maximumFractionDigits: 0,
        }).format(Math.floor(Number(price)));
    };

    const handleOpenModal = (episodeDetails: DetailsObject) => {
        if (episodeDetails) {
            openInfoModal({
                resolution: episodeDetails.resolution,
                aspectRatio: episodeDetails.aspectRatio,
                videoBitrate: episodeDetails.videoBitrate,
                audioBitrate: episodeDetails.audioBitrate,
                audioSampleRate: episodeDetails.audioSampleRate,
                audioMode: episodeDetails.audioMode,
            });
        }
    };

    if (loadingInitial) {
        return (
            <div className={containerClassName}>
                {episodeSkeletonKeys.map((key) => (
                    <Skeleton key={key} className="mb-3 h-[88px] w-full bg-white/10 rounded-[6px]" />
                ))}
            </div>
        );
    }

    if (!episodes || episodes.length === 0) {
        return null;
    }

    const totalEpisodesCount = totalCount ?? episodes.length;
    const selectedEpisodesCount = Math.max(totalEpisodesCount - deselectedEpisodesIds.length, 0);

    const handleCheckboxClick = (episode: GraphQLProgram) => {
        const isDeselected = deselectedEpisodesIds.includes(episode.identifier.id);

        if (!isDeselected && selectedEpisodesCount <= 1) {
            return;
        }

        const newDeselectedIds = isDeselected
            ? deselectedEpisodesIds.filter((id) => id !== episode.identifier.id)
            : [...deselectedEpisodesIds, episode.identifier.id];

        onSelectedEpisodesChange(newDeselectedIds);
    };

    return (
        <div className={containerClassName}>
            <DndContext onDragEnd={handleDragEnd} sensors={sensors}>
                <SortableContext items={episodes.map((ep) => ep.identifier.id)} strategy={verticalListSortingStrategy}>
                    {episodes.map((episode: GraphQLProgram) => (
                        <SortableItem key={episode.identifier.id} id={episode.identifier.id} disabled={!isEditView}>
                            {({ listeners }) => {
                                const episodeDetails = episode.masterFile?.details as DetailsObject;
                                const isTechnicalInfoActive = checkDetailsAvailability(episode);
                                const isEpisodeDeselected = deselectedEpisodesIds.includes(episode.identifier.id);
                                const isLastSelectedEpisode = !isEpisodeDeselected && selectedEpisodesCount <= 1;
                                const canSelectEpisode = !isReadOnly && !isEditView && !isAnon && !isSeller;
                                const labelStateClassName = !canSelectEpisode
                                    ? ''
                                    : isLastSelectedEpisode
                                      ? 'cursor-not-allowed'
                                      : 'cursor-pointer';

                                return (
                                    <div className="group flex flex-col md:flex-row justify-between md:items-center gap-5 border-b-2 py-5">
                                        <div
                                            className={`flex flex-col py-1 px-1 rounded-[8px] md:flex-row gap-4 w-full items-center ${isEditView ? editViewEpisodeClassName : ''}`}
                                            {...(isEditView ? listeners : {})}
                                            style={isEditView ? { touchAction: 'none' } : {}}
                                        >
                                            <div className="flex flex-1 min-w-0 gap-4">
                                                <div className="flex items-center min-w-0 font-bold whitespace-nowrap flex-1">
                                                    <label
                                                        htmlFor={
                                                            canSelectEpisode
                                                                ? `episode-checkbox-${episode.identifier.id}`
                                                                : undefined
                                                        }
                                                        className={`flex text-left ${labelStateClassName} pr-[10px] items-center`}
                                                    >
                                                        {canSelectEpisode && (
                                                            <>
                                                                <input
                                                                    id={`episode-checkbox-${episode.identifier.id}`}
                                                                    type="checkbox"
                                                                    className="sr-only peer"
                                                                    checked={!isEpisodeDeselected}
                                                                    disabled={isLastSelectedEpisode}
                                                                    onChange={() => handleCheckboxClick(episode)}
                                                                />
                                                                <CustomCheckBox
                                                                    customClassName="relative mr-[10px]"
                                                                    disabled={isLastSelectedEpisode}
                                                                />{' '}
                                                            </>
                                                        )}
                                                        <span className="inline-block mr-1">
                                                            Ep {episode.episodeNumber}
                                                            {isEditView
                                                                ? ' - '
                                                                : episode.titles[locale as LanguageIsoCode] !==
                                                                    mainProgramTitle
                                                                  ? ' - '
                                                                  : ''}
                                                        </span>
                                                        <span className="min-w-0 overflow-hidden text-ellipsis">
                                                            {isEditView ? (
                                                                <>
                                                                    {Object.entries(episode.titles || {}).map(
                                                                        ([lang]) => (
                                                                            <span
                                                                                ref={titleRef}
                                                                                className="w-full"
                                                                                key={lang}
                                                                            >
                                                                                <div
                                                                                    className={
                                                                                        locale !== lang ? 'hidden' : ''
                                                                                    }
                                                                                >
                                                                                    <AutoResizeInput
                                                                                        maxWidth="100%"
                                                                                        value={getMultiLanguageValue(
                                                                                            episode,
                                                                                            'titles',
                                                                                            locale,
                                                                                        )}
                                                                                        parentRef={titleRef}
                                                                                        currentLocale={locale}
                                                                                        className={`w-full mr-1 px-[10px] text-[16px] font-bold ${formInputClassName}`}
                                                                                        onChange={(e) => {
                                                                                            const currentValue =
                                                                                                getMultiLanguageValue(
                                                                                                    episode,
                                                                                                    'titles',
                                                                                                    lang,
                                                                                                );
                                                                                            let newValue =
                                                                                                e.target.value;
                                                                                            if (newValue === '') {
                                                                                                newValue = currentValue;
                                                                                            }
                                                                                            e.target.value = newValue;
                                                                                        }}
                                                                                        onBlur={(e) => {
                                                                                            const currentValue =
                                                                                                getMultiLanguageValue(
                                                                                                    episode,
                                                                                                    'titles',
                                                                                                    locale,
                                                                                                );
                                                                                            const newValue =
                                                                                                e.target.value.trim();

                                                                                            if (newValue === '') {
                                                                                                e.target.value =
                                                                                                    currentValue;
                                                                                                return;
                                                                                            }

                                                                                            updateEpisodeTitle(
                                                                                                newValue,
                                                                                                locale as LanguageIsoCode,
                                                                                                episode,
                                                                                                episodes,
                                                                                            );
                                                                                        }}
                                                                                    />
                                                                                </div>
                                                                            </span>
                                                                        ),
                                                                    )}
                                                                </>
                                                            ) : episode.titles[locale as LanguageIsoCode] !==
                                                              mainProgramTitle ? (
                                                                episode.titles[locale as LanguageIsoCode]
                                                            ) : (
                                                                ''
                                                            )}
                                                        </span>
                                                        {episode.duration ? (
                                                            <span className="ml-1">
                                                                {t('metaData.programLengthValueFull', {
                                                                    programDuration: formatDuration(episode.duration),
                                                                    programQuality: episode.quality as string,
                                                                })}
                                                            </span>
                                                        ) : (
                                                            ''
                                                        )}
                                                    </label>
                                                </div>
                                                <div className="flex">
                                                    {isTechnicalInfoActive && (
                                                        <Image
                                                            src={iconInfoWhite.src}
                                                            alt="icon info"
                                                            className="cursor-pointer mr-[16px]"
                                                            width={48}
                                                            height={48}
                                                            onClick={() => handleOpenModal(episodeDetails)}
                                                        />
                                                    )}
                                                    {isAnon ? (
                                                        <div className="flex-shrink-0 ms-[auto]">
                                                            <Image
                                                                src={iconPlayBorderWhite.src}
                                                                alt={t('buttonVideoLabel')}
                                                                className="cursor-pointer"
                                                                width={48}
                                                                height={48}
                                                                onClick={() => setIsNotRegistered(true)}
                                                            />
                                                        </div>
                                                    ) : episode.masterFile ? (
                                                        <div className="flex-shrink-0 ms-[auto]">
                                                            <Image
                                                                src={iconPlayBorderWhite.src}
                                                                alt={t('buttonVideoLabel')}
                                                                className="cursor-pointer"
                                                                width={48}
                                                                height={48}
                                                                onClick={() => playMaster(episode)}
                                                            />
                                                        </div>
                                                    ) : undefined}
                                                </div>
                                            </div>
                                            {!isReadOnly ? (
                                                isEditView ? (
                                                    <div className="flex items-center gap-2 pointer-events-none">
                                                        <IconDragAndDrop className="opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                                                    </div>
                                                ) : showPurchaseButton ? (
                                                    <div
                                                        className="flex-shrink-0 w-full flex"
                                                        style={{ maxWidth: buttonMinW }}
                                                    >
                                                        <AppButton
                                                            customClassName="w-full justify-center md-w-[100%] text-[14px] font-medium py-[15px] px-[0]"
                                                            innerText={
                                                                episode.price
                                                                    ? t('buttonLabel', {
                                                                          price: formatProgramPrice(
                                                                              locale.toLowerCase(),
                                                                              episode.price,
                                                                          ),
                                                                      })
                                                                    : t('buttonLabelNoPrice')
                                                            }
                                                            btnBgColor="#DD06AE"
                                                            onClick={() => {
                                                                if (user) {
                                                                    setSelectedEpisode(episode);
                                                                    setShowModalCart(true);
                                                                } else {
                                                                    setIsNotRegistered(true);
                                                                }
                                                            }}
                                                        />
                                                    </div>
                                                ) : undefined
                                            ) : null}
                                        </div>
                                    </div>
                                );
                            }}
                        </SortableItem>
                    ))}
                </SortableContext>
            </DndContext>
            {loadingMore && (
                <div className="flex justify-center py-5">
                    <IconLoading />
                </div>
            )}
        </div>
    );
};
